import { StorageKind, StorageSubscribe } from '@revelio/core';

import { savePreferencesForScope } from '../api/user-preferences-api';
import {
  PreferenceScope,
  SELF_MANAGED_PREFERENCE_KEYS,
  type UserPreferenceKey,
  type UserPreferences,
} from '../types';
import { getDefaultPreferences } from './preference-defaults';
import { filterPreferencesForScope } from './preference-helpers';
import { PreferenceBrowserStorage } from './preference-session-storage';

// Broadcast key to notify other hook instances that a successful server sync occurred
export const getPreferenceSavedSyncKey = (scope: PreferenceScope) =>
  `user-preferences-${scope}-saved-sync`;

/**
 * Saves the currently visible preferences as the new defaults.
 * This function:
 * 1. Extracts only visible preferences from the effective UI preferences
 * 2. Merges them into local storage
 * 3. Persists only visible keys to the server
 * 4. Refreshes saved preferences and notifies listeners
 */
export async function saveVisiblePreferencesAsDefault(
  affectedScope: PreferenceScope,
  effectivePreferences: UserPreferences,
  visiblePreferenceKeys: UserPreferenceKey[],
  refetchSavedPreferences: () => Promise<void>
): Promise<void> {
  // Extract only visible preferences, skipping self-managed ones (own save UX)
  const visibleEffectiveEntries = Object.entries(effectivePreferences).filter(
    ([key]) =>
      visiblePreferenceKeys.includes(key as UserPreferenceKey) &&
      !SELF_MANAGED_PREFERENCE_KEYS.includes(key as UserPreferenceKey)
  );
  const visibleEffective = Object.fromEntries(
    visibleEffectiveEntries
  ) as Partial<UserPreferences>;

  if (Object.keys(visibleEffective).length === 0) {
    throw new Error('No visible preferences to save');
  }

  // Merge visible preferences into local storage
  const globalStored = PreferenceBrowserStorage.get(PreferenceScope.GLOBAL);
  const currentStored = PreferenceBrowserStorage.get(affectedScope);
  const mergedStored = {
    ...getDefaultPreferences(affectedScope),
    ...globalStored,
    ...currentStored,
    ...visibleEffective,
  } as UserPreferences;

  PreferenceBrowserStorage.set(affectedScope, mergedStored);
  PreferenceBrowserStorage.set(PreferenceScope.GLOBAL, mergedStored);

  // Persist only visible preferences to server
  const affectedToSave = filterPreferencesForScope(
    affectedScope,
    visibleEffective
  );
  if (Object.keys(affectedToSave).length > 0) {
    await savePreferencesForScope(affectedScope, affectedToSave);
  }

  const globalToSave = filterPreferencesForScope(
    PreferenceScope.GLOBAL,
    visibleEffective
  );
  if (Object.keys(globalToSave).length > 0) {
    await savePreferencesForScope(PreferenceScope.GLOBAL, globalToSave);
  }

  // Refresh saved preferences immediately
  await refetchSavedPreferences();

  // Notify other instances to refresh
  StorageSubscribe.notify(
    StorageKind.Session,
    getPreferenceSavedSyncKey(affectedScope)
  );
}

/**
 * Restores visible preferences to their default values.
 * This function:
 * 1. Uses saved defaults if available, otherwise falls back to hard defaults
 * 2. Merges only visible keys with current stored values
 * 3. Updates local storage and returns the applied defaults
 */
export function restoreVisiblePreferencesToDefaults(
  affectedScope: PreferenceScope,
  savedDefaults: UserPreferences,
  visiblePreferenceKeys: UserPreferenceKey[]
): { appliedDefaults: UserPreferences; mergedPreferences: UserPreferences } {
  // Prefer saved defaults if present, otherwise use hard defaults
  const effectiveDefaults =
    savedDefaults && Object.keys(savedDefaults).length > 0
      ? savedDefaults
      : getDefaultPreferences(affectedScope);

  // Start with current stored values merged with hard defaults
  const globalStored = PreferenceBrowserStorage.get(PreferenceScope.GLOBAL);
  const currentStored = PreferenceBrowserStorage.get(affectedScope);
  const mergedAll = {
    ...getDefaultPreferences(affectedScope),
    ...globalStored,
    ...currentStored,
  } as UserPreferences;

  // Overwrite only visible keys with defaults, skipping self-managed ones
  visiblePreferenceKeys.forEach((key) => {
    if (SELF_MANAGED_PREFERENCE_KEYS.includes(key)) return;
    mergedAll[key] = effectiveDefaults[key as keyof UserPreferences] as never;
  });

  // Write merged preferences
  PreferenceBrowserStorage.set(affectedScope, mergedAll);
  PreferenceBrowserStorage.set(PreferenceScope.GLOBAL, mergedAll);

  return {
    appliedDefaults: effectiveDefaults,
    mergedPreferences: mergedAll,
  };
}
